<template>
    <div class="flex flex-col gap-6">
        <h1 v-if="!embedded" class="title-body pt-10 px-10 hidden md:inline">Chat Summary</h1>
        <Transition name="fade" mode="out-in">
            <div v-if="isLoadingEmailSummary" class="flex flex-col gap-2 min-h-60" :class="{ 'px-10': !embedded }">
                <template v-for="i in 4" :key="i">
                    <SkeletonLoader class="w-full h-4 rounded-md" />
                </template>
                <SkeletonLoader class="w-60 h-4 rounded-md" />
            </div>
            <div v-else-if="!!emailSummaryError">
                <div class="flex flex-col gap-2 items-center mt-auto" :class="{ 'md:pb-10': !embedded }">
                    <div role="alert" class="text-status-error body-semibold max-w-80">{{ emailSummaryError }}</div>
                    <BaseButton theme="tertiary" @click="generateEmailSummary">Retry</BaseButton>
                </div>
            </div>
            <MarkdownText
                v-else
                class="w-full body-small text-ink-muted overflow-y-auto max-h-60"
                :class="{ 'border-2 border-border md:border-none p-6 md:px-10 md:py-0 rounded-xl md:rounded-none': !embedded }"
                :text="summaryText" />
        </Transition>
        <div class="flex flex-col gap-2 body mt-auto" :class="embedded ? 'items-end' : 'items-center md:pb-10'">
            <HorizontalDivider v-if="!embedded" class="border-2 hidden md:block" />
            <BaseButton v-if="!hideEmailButton" theme="primary" :disabled="isSending || isEmailSent || isLoadingEmailSummary" @click="sendEmailSummary">
                <template v-if="isSending">
                    <div class="flex items-center gap-2"><LoadingSpinner width="16" height="16" /> Email Sending</div>
                </template>
                <template v-else-if="isEmailSent"> Email Sent! </template>
                <template v-else-if="isEmailSendError">
                    <div class="flex items-center gap-2"><i class="bi bi-arrow-clockwise" aria-hidden="true" /> Try Again</div>
                </template>
                <template v-else>
                    <div class="flex items-center gap-2"><i class="bi bi-send" aria-hidden="true" /> Email Summary</div>
                </template>
            </BaseButton>
            <BaseButton v-if="showContinueButton" theme="tertiary" @click="$emit('continue')">Wrap up</BaseButton>
        </div>
    </div>
</template>

<script setup>
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { CHAT_EVENT } from "~vue/events";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { inject, onMounted, ref } from "vue";

const { hideEmailButton, showContinueButton } = defineProps({
    hideEmailButton: { type: Boolean, default: false },
    showContinueButton: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
});

defineEmits(["continue"]);

const { $sendEvent } = inject("globalProperties");
const coachingSessionId = inject("coachingSessionId");

const isLoadingEmailSummary = ref(true);
const summaryText = ref("");
const emailSummaryError = ref("");

const generateEmailSummary = async () => {
    isLoadingEmailSummary.value = true;
    emailSummaryError.value = "";
    try {
        const { data, error } = await useFetch("/events/generate_chat_summary")
            .post({
                cs_id: coachingSessionId,
            })
            .json();
        if (error.value) {
            throw new Error(error.value.message || "Failed to generate chat summary");
        }
        summaryText.value = data.value.payload;
    } catch {
        emailSummaryError.value = "Oops! Something went wrong with creating an email summary, please try again.";
    } finally {
        isLoadingEmailSummary.value = false;
    }
};

onMounted(generateEmailSummary);

const isSending = ref(false);
const isEmailSent = ref(false);
const isEmailSendError = ref(false);

const sendEmailSummary = async () => {
    try {
        isSending.value = true;
        await $sendEvent(CHAT_EVENT.EMAIL_CHAT_SUMMARY, { content: summaryText.value });
        isEmailSent.value = true;
    } catch {
        isEmailSendError.value = true;
    } finally {
        isSending.value = false;
    }
};
</script>

<style scoped>
.iconBold {
    -webkit-text-stroke: 1px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
